import { Link, useNavigate } from "react-router-dom";
import { LogOut, Home } from "lucide-react";
import Logo from "../Logo";
import { useAuth } from "../../lib/auth";
import { useLang } from "../../lib/i18n";

export const Card = ({ title, sub, children, className = "", testid }) => (
  <section data-testid={testid} className={`rounded-3xl border border-white/10 bg-panel/70 p-6 sm:p-8 ${className}`}>
    {title && <h2 className="font-display font-bold text-white text-xl">{title}</h2>}
    {sub && <p className="text-slate-400 text-sm mt-1.5 leading-relaxed">{sub}</p>}
    <div className={title ? "mt-6" : ""}>{children}</div>
  </section>
);

export const Pill = ({ tone = "slate", children, testid }) => {
  const tones = {
    slate: "border-white/15 text-slate-300",
    gold: "border-gold/50 text-gold bg-gold/10",
    mint: "border-mint/50 text-mint bg-mint/10",
    red: "border-red-400/50 text-red-300 bg-red-400/10",
  };
  return (
    <span data-testid={testid} className={`inline-flex items-center rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${tones[tone]}`}>
      {children}
    </span>
  );
};

export const Btn = ({ children, variant = "gold", className = "", ...props }) => {
  const v = {
    gold: "bg-gold text-ink hover:bg-white disabled:opacity-50",
    ghost: "border border-white/20 text-white hover:border-gold hover:text-gold disabled:opacity-40",
    danger: "border border-red-400/40 text-red-300 hover:bg-red-400/10 disabled:opacity-40",
  };
  return (
    <button {...props} className={`inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm px-5 py-2.5 transition-colors duration-300 ${v[variant]} ${className}`}>
      {children}
    </button>
  );
};

export const Input = (props) => (
  <input
    {...props}
    className={`w-full rounded-xl border border-white/15 bg-ink/60 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-gold/60 ${props.className || ""}`}
  />
);

export const Select = ({ children, ...props }) => (
  <select {...props} className={`w-full rounded-xl border border-white/15 bg-ink/60 px-4 py-3 text-sm text-white focus:outline-none focus:border-gold/60 ${props.className || ""}`}>
    {children}
  </select>
);

export const Toggle = ({ on, onChange, testid, disabled }) => (
  <button
    type="button"
    data-testid={testid}
    role="switch"
    aria-checked={on}
    disabled={disabled}
    onClick={() => onChange(!on)}
    className={`relative h-7 w-12 rounded-full border transition-colors duration-300 disabled:opacity-40 ${on ? "bg-mint/80 border-mint" : "bg-ink border-white/20"}`}
  >
    <span className={`absolute top-0.5 h-5.5 w-5.5 h-[22px] w-[22px] rounded-full bg-white transition-transform duration-300 ${on ? "translate-x-[22px]" : "translate-x-0.5"}`} />
  </button>
);

export default function Shell({ title, children }) {
  const { user, logout } = useAuth();
  const { lang, setLang } = useLang();
  const navigate = useNavigate();
  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };
  return (
    <div className="min-h-screen arch-grid">
      <header className="sticky top-0 z-40 bg-ink/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between gap-4">
          <Link to="/" data-testid="dash-logo"><Logo /></Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="hidden sm:block text-sm text-slate-300 truncate max-w-[180px]" data-testid="dash-user-name">{user?.name}</span>
            {user?.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-white/20" />}
            <button data-testid="dash-lang-toggle" onClick={() => setLang(lang === "en" ? "te" : "en")} className="rounded-full border border-gold/40 px-3 py-1.5 font-mono text-[10px] text-gold">
              {lang === "en" ? "TE" : "EN"}
            </button>
            <Link to="/" data-testid="dash-home-link" className="p-2 text-slate-300 hover:text-gold"><Home size={18} /></Link>
            <button data-testid="dash-logout-button" onClick={doLogout} className="p-2 text-slate-300 hover:text-red-300"><LogOut size={18} /></button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <h1 data-testid="dash-title" className="font-display font-extrabold text-3xl sm:text-4xl text-white">{title}</h1>
        <div className="mt-8">{children}</div>
      </main>
    </div>
  );
}
